import { Box } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { DataGridColumnHeaderWithToolTip } from '@verantos-dev/shared-components';
import { stringCellStyles } from '@verantos-dev/shared-styles';
import { IsValidCreateUploadResponse } from '@verantos-dev/types-concepts';

export const useValidationResultsGrid = (concepts?: IsValidCreateUploadResponse[]) => {
  const intl = useIntl();

  const conceptsSorted = useMemo(() => {
    const sorted = [...(concepts || [])];
    sorted.sort((a, b) => {
      if (a.isValid === b.isValid) return 0;
      return a.isValid ? 1 : -1;
    });
    return sorted;
  }, [concepts]);

  const columns: GridColDef<IsValidCreateUploadResponse>[] = useMemo(() => {
    const standardConceptColumnHeaderName = intl.formatMessage({
      id: 'conceptGroups.validationResults.columns.standard',
    });
    const standardConceptTooltipText = intl.formatMessage({
      id: 'conceptGroups.validationResults.standardConceptTooltip',
    });

    return [
      {
        field: 'conceptId',
        headerName: intl.formatMessage({
          id: 'conceptGroups.validationResults.columns.omopConceptId',
        }),
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'conceptName',
        headerName: intl.formatMessage({
          id: 'conceptGroups.validationResults.columns.conceptName',
        }),
        flex: 1.5,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box sx={stringCellStyles}>
              {params.row.conceptName ||
                intl.formatMessage({ id: 'conceptGroups.validationResults.notFound' })}
            </Box>
          );
        },
      },
      {
        field: 'standard',
        headerName: standardConceptColumnHeaderName,
        renderHeader: () => (
          <DataGridColumnHeaderWithToolTip
            id="concept-group-validation-results-standard"
            headerName={standardConceptColumnHeaderName}
            description={standardConceptTooltipText}
            placement="top"
          />
        ),
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as IsValidCreateUploadResponse;
          if (!row.isValid) {
            return '--';
          }

          const isStandard = Boolean(row.standardConcept === 'S');

          return isStandard
            ? intl.formatMessage({ id: 'conceptGroups.validationResults.yes' })
            : intl.formatMessage({ id: 'conceptGroups.validationResults.no' });
        },
      },
      {
        field: 'vocabularyId',
        headerName: intl.formatMessage({
          id: 'conceptGroups.validationResults.columns.vocabulary',
        }),
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => params.row.vocabularyId || '--',
      },
      {
        field: 'conceptCode',
        headerName: intl.formatMessage({
          id: 'conceptGroups.validationResults.columns.conceptCode',
        }),
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => params.row.conceptCode || '--',
      },
      {
        field: 'domainId',
        headerName: intl.formatMessage({ id: 'conceptGroups.validationResults.columns.domain' }),
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => params.row.domainId || '--',
      },
    ];
  }, [intl]);

  return {
    conceptsSorted,
    columns,
  };
};
